import recentlyViewedStorage from './recently-viewed-storage.js'

const SESSION_SHOWN_KEY = 'rv-drawer:session-shown'
const SESSION_ELIGIBLE_KEY = 'rv-drawer:auto-open-eligible'
const SUPPRESSED_UNTIL_KEY = 'rv-drawer:suppressed-until'
const MS_PER_DAY = 86400000
const DRAWER_ID = 'recently-viewed-drawer'
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll']

function readStorage(storage, key) {
  try {
    return storage.getItem(key)
  } catch (e) {
    return null
  }
}

function writeStorage(storage, key, value) {
  try {
    storage.setItem(key, value)
  } catch (e) {
    // Storage may be unavailable; fail silently.
  }
}

function isElementVisible(el) {
  if (!el) return false
  if (el.getAttribute('aria-hidden') === 'true') return false

  const style = window.getComputedStyle(el)
  if (
    style.display === 'none' ||
    style.visibility === 'hidden' ||
    style.opacity === '0'
  ) {
    return false
  }

  const rect = el.getBoundingClientRect()
  return rect.width > 0 && rect.height > 0
}

/**
 * Attentive keeps #attentive_overlay at height:0 always. The iframe
 * #attentive_creative is fullscreen (inset:0 / 100%) while open, and
 * collapses to a small corner teaser after dismiss — only the fullscreen
 * state should block auto-open.
 */
function isAttentiveBlocking() {
  const creative = document.querySelector('#attentive_creative')
  if (!creative) return false

  const style = window.getComputedStyle(creative)
  if (style.visibility === 'hidden' || style.opacity === '0') return false

  const rect = creative.getBoundingClientRect()
  return (
    rect.width >= window.innerWidth * 0.5 &&
    rect.height >= window.innerHeight * 0.5
  )
}

function isOneTrustBlocking() {
  return isElementVisible(document.querySelector('#onetrust-banner-sdk'))
}

function parseConfig(el) {
  const script = el.querySelector(
    'script[type="application/json"][data-rv-drawer-config]'
  )
  if (!script) return {}

  try {
    return JSON.parse(script.textContent || '{}') || {}
  } catch (e) {
    return {}
  }
}

class RecentlyViewedDrawer extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-recently-viewed-list]')
    this.excludeHandle = this.getAttribute('exclude-product-handle') || ''
    this.hhDrawer =
      this.closest('hh-drawer') || document.getElementById(DRAWER_ID)
    this.mobileQuery = window.matchMedia('(max-width: 767px)')

    const config = parseConfig(this)
    this.desktopLimit = Number(config.limitDesktop) || 20
    this.mobileLimit = Number(config.limitMobile) || 8
    this.minimumProducts = Number(config.minimumProducts) || 4
    this.autoOpen = Boolean(config.autoOpen)
    this.autoOpenDelay = Number(config.autoOpenDelay) || 10
    this.autoCloseDelay = Number(config.autoCloseDelay) || 0
    this.dismissCooldownDays = Number(config.dismissCooldownDays) || 0
    this.disabledTemplates = String(config.disabledTemplates || '')
    this.template = String(config.template || '')

    this.openedByAuto = false
    this.openTimer = null
    this.closeTimer = null

    this.onDrawerOpened = this.onDrawerOpened.bind(this)
    this.onActivity = this.onActivity.bind(this)
    this.onOpenAttributeChange = this.onOpenAttributeChange.bind(this)
    this.onCloseButtonClick = this.onCloseButtonClick.bind(this)
    this.onBreakpointChange = this.onBreakpointChange.bind(this)

    window.addEventListener('drawer:opened', this.onDrawerOpened)
    this.mobileQuery.addEventListener('change', this.onBreakpointChange)

    this.closeButton = this.hhDrawer?.querySelector('[slot="close"]')
    this.closeButton?.addEventListener('click', this.onCloseButtonClick)

    this.populate()
    this.observeDrawerOpen()
    this.resolveSessionEligibility()
    this.scheduleAutoOpen()
  }

  disconnectedCallback() {
    window.removeEventListener('drawer:opened', this.onDrawerOpened)
    this.mobileQuery.removeEventListener('change', this.onBreakpointChange)
    this.closeButton?.removeEventListener('click', this.onCloseButtonClick)
    this.clearOpenTimer()
    this.clearCloseTimer()
    this.unbindActivityListeners()
    this.openObserver?.disconnect()
  }

  get limit() {
    return this.mobileQuery.matches ? this.mobileLimit : this.desktopLimit
  }

  getStoredHandles() {
    return recentlyViewedStorage.list().filter(Boolean)
  }

  getHandles() {
    return this.getStoredHandles().filter(
      (handle) => handle !== this.excludeHandle
    )
  }

  getDisplayHandles() {
    return this.getHandles().slice(0, this.limit)
  }

  onBreakpointChange() {
    this.populate()
  }

  onDrawerOpened(event) {
    if (event.detail?.id !== DRAWER_ID) return

    // Any open counts as this session's showing, including a manual open from
    // the "see more" trigger, so a pending auto-open never fires afterwards.
    this.clearOpenTimer()
    writeStorage(sessionStorage, SESSION_SHOWN_KEY, '1')

    this.populate()

    if (this.openedByAuto && this.autoCloseDelay > 0) {
      this.bindActivityListeners()
      this.resetCloseTimer()
    }
  }

  populate() {
    if (!this.list) return

    const handles = this.getDisplayHandles()

    if (handles.length === 0) {
      this.list.innerHTML = ''
      return
    }

    this.list.innerHTML = handles
      .map(
        (handle) => `
      <li class='recently-viewed-drawer__item'>
        <dynamic-product-card handle='${handle}' template='product-card'></dynamic-product-card>
      </li>
    `
      )
      .join('')
  }

  // --- Auto-open gates ---

  isTemplateDisabled() {
    const disabled = this.disabledTemplates
      .split(',')
      .map((name) => name.trim().toLowerCase())
      .filter(Boolean)

    if (!disabled.length || !this.template) return false
    return disabled.includes(this.template.toLowerCase())
  }

  hasSessionShown() {
    return Boolean(readStorage(sessionStorage, SESSION_SHOWN_KEY))
  }

  // Prevent auto-open if there are no eligible products on first visit.
  // Counts the whole stored list rather than getHandles(): the flag is locked
  // for the session, so it must not depend on the excludeHandle of whichever
  // page the session happened to start on.
  resolveSessionEligibility() {
    if (readStorage(sessionStorage, SESSION_ELIGIBLE_KEY) !== null) return

    const eligible = this.getStoredHandles().length >= this.minimumProducts
    writeStorage(sessionStorage, SESSION_ELIGIBLE_KEY, eligible ? '1' : '0')
  }

  isSessionEligible() {
    return readStorage(sessionStorage, SESSION_ELIGIBLE_KEY) === '1'
  }

  isSuppressed() {
    const raw = readStorage(localStorage, SUPPRESSED_UNTIL_KEY)
    if (!raw) return false

    const until = Number(raw)
    if (!Number.isFinite(until)) return false
    return until > Date.now()
  }

  hasConflictingOverlay() {
    if (document.querySelector('hh-drawer[open]')) {
      return true
    }

    return isOneTrustBlocking() || isAttentiveBlocking()
  }

  canScheduleAutoOpen() {
    if (!this.autoOpen) return false
    if (this.isTemplateDisabled()) return false
    if (!this.isSessionEligible()) return false
    if (this.hasSessionShown()) return false
    if (this.isSuppressed()) return false
    if (this.getHandles().length < this.minimumProducts) return false
    return true
  }

  canOpenNow() {
    if (!this.isSessionEligible()) return false
    if (this.hasSessionShown()) return false
    if (this.isSuppressed()) return false
    if (this.hasConflictingOverlay()) return false
    if (this.getHandles().length < this.minimumProducts) return false
    return true
  }

  scheduleAutoOpen() {
    if (!this.canScheduleAutoOpen()) return

    this.clearOpenTimer()
    this.openTimer = setTimeout(() => {
      this.openTimer = null
      this.tryAutoOpen()
    }, this.autoOpenDelay * 1000)
  }

  tryAutoOpen() {
    if (!this.canOpenNow()) return

    this.openedByAuto = true

    window.dispatchEvent(
      new CustomEvent('drawer', {
        bubbles: true,
        composed: true,
        detail: { id: DRAWER_ID },
      })
    )
  }

  // --- Auto-close (inactivity) ---

  bindActivityListeners() {
    if (!this.hhDrawer || this.activityBound) return
    this.activityBound = true
    ACTIVITY_EVENTS.forEach((type) => {
      this.hhDrawer.addEventListener(type, this.onActivity, {
        capture: true,
        passive: true,
      })
    })
  }

  unbindActivityListeners() {
    if (!this.hhDrawer || !this.activityBound) return
    ACTIVITY_EVENTS.forEach((type) => {
      this.hhDrawer.removeEventListener(type, this.onActivity, {
        capture: true,
      })
    })
    this.activityBound = false
  }

  onActivity() {
    if (!this.openedByAuto || this.autoCloseDelay <= 0) return
    this.resetCloseTimer()
  }

  resetCloseTimer() {
    this.clearCloseTimer()
    if (this.autoCloseDelay <= 0) return

    this.closeTimer = setTimeout(() => {
      this.closeTimer = null
      this.autoCloseDrawer()
    }, this.autoCloseDelay * 1000)
  }

  autoCloseDrawer() {
    if (!this.hhDrawer?.open) return
    this.hhDrawer.closeDrawer?.()
  }

  // --- Dismissal ---

  // Cool-down is written only for a deliberate dismissal via the close
  // button. Auto-close, Escape, overlay clicks and closes triggered by
  // another drawer opening all leave the cool-down untouched.
  onCloseButtonClick() {
    if (this.dismissCooldownDays <= 0) return

    const until = Date.now() + this.dismissCooldownDays * MS_PER_DAY
    writeStorage(localStorage, SUPPRESSED_UNTIL_KEY, String(until))
  }

  observeDrawerOpen() {
    if (!this.hhDrawer) return

    this.openObserver = new MutationObserver(this.onOpenAttributeChange)
    this.openObserver.observe(this.hhDrawer, {
      attributes: true,
      attributeFilter: ['open'],
    })
  }

  onOpenAttributeChange() {
    if (this.hhDrawer?.hasAttribute('open')) return

    this.clearCloseTimer()
    this.unbindActivityListeners()
    this.openedByAuto = false
  }

  clearOpenTimer() {
    if (this.openTimer) {
      clearTimeout(this.openTimer)
      this.openTimer = null
    }
  }

  clearCloseTimer() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer)
      this.closeTimer = null
    }
  }
}

customElements.define('recently-viewed-drawer', RecentlyViewedDrawer)
